import { EmojiIcon } from '@mote/assets/icons'
import { REACTION_EMOJIS } from '@mote/helpers/constants'
import { useReactionsContext } from '@mote/recipes'
import { Dropdown, VisuallyHidden } from '@mote/ui/components'
import styled, { th } from '@xstyled/styled-components'
import { Emoji } from '../../shared/components'

interface EmojiPickerProps {
  moteId: string
}

export function EmojiPicker(props: EmojiPickerProps): JSX.Element {
  const reactions = useReactionsContext()
  const activeEmojis = REACTION_EMOJIS.filter(emoji =>
    reactions.isEnabled(emoji.value)
  )

  return (
    <Root aria-label='Reactions' role='region'>
      <VisuallyHidden>Reactions</VisuallyHidden>
      {activeEmojis.length > 0 ? (
        <ActiveEmojisContainer aria-label='Active reactions'>
          <VisuallyHidden>Active reactions</VisuallyHidden>
          {activeEmojis.map(({ emoji, description, value }) => (
            <Emoji
              isInteractive={false}
              key={value}
              message={description}
              onClick={(event: MouseEvent) => {
                event.stopPropagation()
                reactions.react(value)
              }}
              isAnimated={false}
              noTooltip
            >
              {emoji}
            </Emoji>
          ))}
        </ActiveEmojisContainer>
      ) : null}
      <StyledDropdown
        id={`mote-player-reactions-${props.moteId}`}
        onAction={handleSelection}
        triggerElementContent={<EmojiIcon />}
        placement='top-right'
        showChevron={false}
        aria-label='Mote reactions'
        orientation='horizontal'
      >
        {REACTION_EMOJIS.map(({ emoji, value, description, label }) => {
          return (
            <DropdownItem key={value}>
              <Emoji
                onClick={undefined}
                message={description}
                label={label}
                isAnimated={false}
                isEmojiPickerItem
              >
                {emoji}
              </Emoji>
            </DropdownItem>
          )
        })}
      </StyledDropdown>
    </Root>
  )

  function handleSelection(reaction: string, event: MouseEvent) {
    event.stopPropagation()
    reactions.react(reaction)
  }
}

const Root = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin: 4px 0;
  width: 100%;
`

const ActiveEmojisContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const StyledDropdown = styled(Dropdown)`
  .dropdown-trigger-container {
    padding: 4px;
    border-radius: 100%;
    color: ${th.color('grey.d2')};

    &:hover {
      color: ${th.color('primary.base')};
    }

    &:active {
      color: ${th.color('primary.d1')};
    }
  }

  .dropdown-container {
    width: max-content;
    padding: 8px;
    border-radius: 24px;
  }
`

const DropdownItem = styled(Dropdown.Item)`
  height: 24px;
  padding: 0;

  span.emoji {
    transition: transform 150ms ease-in;
  }

  &:hover,
  &:focus {
    background: transparent;

    span.emoji {
      cursor: pointer;
      transform: scale(1.25);
    }
  }
`
